/**
 * <sunday-m-see>: "See it for yourself." (sections/sunday-m-see.liquid; AR's go, 24 Oct). Three screens, one row of
 * tabs: Inside (layers to scale beside an inch ruler), Feel (the press clip, firmness on a scale, one line), Tests (the
 * other clips one after another; thin bars on the clip, tap its left or right to skip). Swipe or tap the tabs.
 * Inline on the page; full screen from "See inside" on the main photo or "Full screen" here, with the size, price and
 * Add to cart (the buy box's own Add is pressed). Clips load only on their screen. The thickness picked in the buy box
 * (sunday:buy) changes the inches. Words come from the section.
 */
import { esc, fill } from '@sunday/util';

const ORDER = { egg: 0, car: 1, jump: 2 };

class SundayMSee extends HTMLElement {
  connectedCallback() {
    if (this.cfg) return;
    const el = this.querySelector('script[type="application/json"]');
    this.cfg = JSON.parse(el.textContent);
    this.full = this.hasAttribute('data-full');
    const c = this.cfg;
    this.base = c.layers.map((l) => l.in);
    this.tests = (c.tests || []).slice().sort((a, b) => (ORDER[a.k] ?? 9) - (ORDER[b.k] ?? 9));
    this.screens = ['inside', c.press ? 'feel' : null, this.tests.length ? 'tests' : null].filter(Boolean);
    this.render();
    if (!this.full) {
      this.#chip();
      document.querySelector('[data-see-full]')?.addEventListener('click', () => this.open(this.at || 0));
    }
    const apply = (h) => {
      const ins = (c.heights || {})[String(h)];
      c.layers.forEach((l, k) => (l.in = ins ? ins[k] ?? this.base[k] : this.base[k]));
      const s = this.querySelector('[data-stack]');
      if (s) s.innerHTML = this.#stack();
      const tot = this.querySelector('[data-tot]');
      if (tot) tot.textContent = `${c.layers.reduce((a, l) => a + Number(l.in || 0), 0)}″`;
    };
    document.addEventListener('sunday:buy', (e) => { if (!this.isConnected) return; apply(e.detail && e.detail.h); this.#buy(e.detail); });
    if (window.sundayBuyLast) { apply(window.sundayBuyLast.h); this.#buy(window.sundayBuyLast); }
  }

  /* ---------- markup ---------- */
  render() {
    const c = this.cfg, t = c.t;
    const tabs = this.screens.map((k, i) => `<button type="button" role="tab" data-go="${i}" aria-selected="${i === 0}">${esc(t[k])}</button>`).join('');
    const inside = `<div class="sd-see-scr" data-k="inside"><div class="sd-see-tot"><b data-tot></b><span>${esc(t.tall)}</span></div><div data-stack>${this.#stack()}</div></div>`;
    const p = c.press;
    const feel = p ? `<div class="sd-see-scr" data-k="feel"><div class="sd-see-vid"><video muted loop playsinline preload="none" data-src="${esc(p.src)}"${p.poster ? ` poster="${esc(p.poster)}"` : ''} aria-label="${esc(p.cap || '')}"></video></div><div class="sd-see-side"><p class="sd-see-fw"><b>${c.firm}/10</b> ${esc(c.firmWord)}</p><div class="sd-see-scale"><div class="sd-see-bar"><span style="left:${c.firm * 10}%"></span></div><div class="sd-see-ends"><span>${esc(t.soft)}</span><span>${esc(t.firmEnd)}</span></div></div><p class="sd-see-say">${esc(t.feelLine)}</p></div></div>` : '';
    const t0 = this.tests[0];
    const tests = t0 ? `<div class="sd-see-scr" data-k="tests"><div class="sd-see-vid"><video muted playsinline preload="none" data-test data-src="${esc(t0.src)}"${t0.poster ? ` poster="${esc(t0.poster)}"` : ''}></video><div class="sd-see-segs" aria-hidden="true">${this.tests.map(() => '<i><b></b></i>').join('')}</div><button type="button" class="sd-see-nav sd-see-prev" aria-label="${esc(t.prev)}"></button><button type="button" class="sd-see-nav sd-see-next" aria-label="${esc(t.next)}"></button></div><div class="sd-see-side"><p class="sd-see-say" data-say aria-live="polite"></p></div></div>` : '';
    const top = this.full ? `<div class="sd-see-top"><h3>${esc(c.name)}</h3><button type="button" class="sd-see-x" data-close aria-label="${esc(t.close)}">×</button></div>` : '';
    const buy = this.full ? `<div class="sd-see-buy"><div><small data-bl></small><b data-bp></b></div><button type="button" class="sd-btn sd-see-add" data-add>${esc(t.add)}</button></div>` : '';
    this.innerHTML = `${top}<div class="sd-see-tabs" role="tablist">${tabs}</div><div class="sd-see-stage" data-stage>${inside}${feel}${tests}</div>${buy}`;
    this.#wire();
  }

  #stack() {
    const c = this.cfg, t = c.t;
    const total = c.layers.reduce((a, l) => a + Number(l.in || 0), 0);
    const ip = 40, top = 34, x0 = 46, w = 128, H = top + total * ip + 14;
    let y = top, o = '';
    o += `<line x1="30" y1="${top}" x2="30" y2="${top + total * ip}" class="sd-see-rl"/>`;
    for (let i = 0; i <= total; i++) {
      const yy = top + i * ip;
      o += `<line x1="${i % 2 ? 25 : 21}" y1="${yy}" x2="30" y2="${yy}" class="sd-see-rl"/>`;
      if (i % 2 === 0) o += `<text x="16" y="${yy + 4}" text-anchor="end" class="sd-see-rt">${i}″</text>`;
    }
    o += `<rect x="${x0 - 3}" y="${top - 3}" width="${w + 6}" height="${total * ip + 6}" rx="9" class="sd-see-cov"/>`;
    o += `<text x="${x0}" y="${top - 12}" class="sd-see-rt">${esc(t.cover)}</text>`;
    c.layers.forEach((l, i) => {
      const h = Number(l.in) * ip;
      o += `<rect x="${x0}" y="${y}" width="${w}" height="${h}" fill="${esc(l.col || '#ddd')}"/>`;
      if (i) o += `<line x1="${x0}" y1="${y}" x2="${x0 + w}" y2="${y}" class="sd-see-sep"/>`;
      const cy = y + h / 2;
      o += `<line x1="${x0 + w + 6}" y1="${cy}" x2="${x0 + w + 16}" y2="${cy}" class="sd-see-rl"/>`;
      o += `<text x="${x0 + w + 20}" y="${cy - 3}" class="sd-see-ln">${esc(`${l.in}″ ${l.nm}`)}</text>`;
      if (l.kg) o += `<text x="${x0 + w + 20}" y="${cy + 13}" class="sd-see-rt">${esc(fill(t.kg, { n: l.kg }))}</text>`;
      y += h;
    });
    return `<svg class="sd-see-stk" viewBox="0 0 360 ${H}" role="img" aria-label="${esc(t.aria)}">${o}</svg>`;
  }

  /* ---------- behaviour ---------- */
  #wire() {
    const st = this.querySelector('[data-stage]');
    const tabs = [...this.querySelectorAll('[data-go]')];
    const tot = this.querySelector('[data-tot]');
    if (tot) tot.textContent = `${this.cfg.layers.reduce((a, l) => a + Number(l.in || 0), 0)}″`;
    const cur = () => Math.round(st.scrollLeft / Math.max(1, st.clientWidth));
    const show = (i) => {
      this.at = i;
      /* inline: the card is as tall as the screen shown (no empty band under a short screen) */
      if (!this.full && st.children[i]) st.style.height = st.children[i].scrollHeight + 'px';
      tabs.forEach((b, j) => b.setAttribute('aria-selected', String(j === i)));
      [...st.children].forEach((s, j) => {
        const v = s.querySelector('video');
        if (!v) return;
        if (j === i && this.isConnected && this.#seen) {
          if (!v.src && v.dataset.src) v.src = v.dataset.src;
          v.play().catch(() => {});
        } else v.pause();
      });
    };
    this.show = show;
    tabs.forEach((b, i) => b.addEventListener('click', () => { st.scrollTo({ left: i * st.clientWidth, behavior: 'smooth' }); show(i); }));
    let tm;
    st.addEventListener('scroll', () => { clearTimeout(tm); tm = setTimeout(() => show(cur()), 90); }, { passive: true });
    /* tests: one after another, bars show where it is; tap left or right of the clip to skip */
    const tv = this.querySelector('[data-test]');
    if (tv) {
      const sg = [...this.querySelectorAll('.sd-see-segs b')];
      const say = this.querySelector('[data-say]');
      const words = (d) => this.cfg.t[d.k] || d.cap || '';
      const paint = () => { const i = +tv.dataset.i; sg.forEach((b, j) => (b.style.width = j < i ? '100%' : j > i ? '0%' : `${(tv.currentTime / (tv.duration || 1)) * 100}%`)); };
      const go = (i) => {
        const d = this.tests[i];
        tv.dataset.i = i;
        tv.poster = d.poster || '';
        tv.src = d.src;
        tv.setAttribute('aria-label', words(d));
        say.textContent = words(d);
        paint();
        tv.play().catch(() => {});
      };
      tv.dataset.i = 0;
      say.textContent = words(this.tests[0]);
      tv.addEventListener('ended', () => go((+tv.dataset.i + 1) % this.tests.length));
      tv.addEventListener('timeupdate', paint);
      this.querySelector('.sd-see-prev').addEventListener('click', () => go((+tv.dataset.i + this.tests.length - 1) % this.tests.length));
      this.querySelector('.sd-see-next').addEventListener('click', () => go((+tv.dataset.i + 1) % this.tests.length));
    }
    /* clips wait until the widget is on screen */
    this.#seen = this.full;
    if (!this.full && 'IntersectionObserver' in window) {
      new IntersectionObserver((es) => {
        this.#seen = es[0].isIntersecting;
        show(this.at || 0);
      }, { threshold: 0.3 }).observe(this);
    }
    this.querySelector('[data-close]')?.addEventListener('click', () => this.close());
    this.querySelector('.sd-see-add')?.addEventListener('click', () => {
      const add = document.querySelector('#add');
      this.close();
      if (add) add.click();
    });
    show(0);
  }
  #seen = false;

  #buy(d) {
    if (!d) return;
    this.querySelectorAll('[data-bl]').forEach((n) => (n.textContent = d.label || ''));
    this.querySelectorAll('[data-bp]').forEach((n) => (n.textContent = d.price || ''));
  }

  /* "See inside" on the main photo (replaces "Turn your phone") */
  #chip() {
    const stage = document.querySelector('.sd-mh-stage');
    if (!stage || stage.querySelector('.sd-see-chip')) return;
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'sd-see-chip';
    b.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="6" width="18" height="12" rx="2.5"/><path d="M3 10h18M3 14h18"/></svg><span>${esc(this.cfg.t.chip)}</span>`;
    ['pointerdown', 'pointerup', 'touchstart'].forEach((n) => b.addEventListener(n, (e) => e.stopPropagation(), { passive: true }));
    b.addEventListener('click', (e) => { e.stopPropagation(); this.open(0); });
    stage.appendChild(b);
  }

  /* ---------- full screen ---------- */
  open(at) {
    if (document.querySelector('sunday-m-see[data-full]')) return;
    const f = document.createElement('sunday-m-see');
    f.setAttribute('data-full', '');
    f.className = 'sd-sw sd-sw-full';
    f.setAttribute('role', 'dialog');
    f.setAttribute('aria-modal', 'true');
    f.setAttribute('aria-label', this.cfg.name);
    f.innerHTML = `<script type="application/json">${JSON.stringify(this.cfg).replace(/</g, '\\u003c')}</script>`;
    this.querySelectorAll('video').forEach((v) => v.pause());
    document.body.appendChild(f);
    document.documentElement.classList.add('sd-see-open');
    f.__from = this;
    requestAnimationFrame(() => {
      const st = f.querySelector('[data-stage]');
      st.scrollLeft = at * st.clientWidth;
      f.show(at);
      f.querySelector('[data-close]')?.focus({ preventScroll: true });
    });
    f.__esc = (e) => e.key === 'Escape' && f.close();
    document.addEventListener('keydown', f.__esc);
  }
  close() {
    if (!this.full) return;
    this.querySelectorAll('video').forEach((v) => v.pause());
    document.removeEventListener('keydown', this.__esc);
    document.documentElement.classList.remove('sd-see-open');
    this.remove();
  }
}
if (!customElements.get('sunday-m-see')) customElements.define('sunday-m-see', SundayMSee);
